import { el, mount, unmount } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';
import externalEventCodes from 'event-system/codes/external';

import eventStatuses from 'constants/event-statuses';

import eventLocationTypes from 'constants/event-location-types';

import { stringToSVG } from 'utilities';

import { checkValueIsURL } from '../utilities';

import modes from '../modes';

import registeredString from 'assets/images/registered-icon.svg?raw';

const registered = stringToSVG(registeredString);

class EventCTAButton {

  constructor(event, mode, alreadyRegistered) {

    this.registerButton = null;
    this.unregisterButton = null;
    this.cancelButton = null;
    this.joinButton = null;
    this.endedButton = null;
    this.cancelledButton = null;

    this.event = event;
    this.mode = mode;
    this.alreadyRegistered = alreadyRegistered;

    this.postId = this.event.postId;

    this.el = el('div.event-control-container');

    this.initialize();

  }

  getJoinButtonText() {

    if (application.isAuthenticated() === false) {
      return 'Join';
    }

    if (application.getUser()._id === this.event.ownerId) {
      return 'Start';
    }

    return 'Join';

  }

  mountRegisterButton() {

    this.registerButton = el('button.event-control', 'Register');
    this.registerButton.addEventListener('click', this.createEventRegistration.bind(this));

    mount(this.el, this.registerButton);

  }

  mountUnregisterButton() {

    const registeredView = el('span.event-check-icon', registered.cloneNode(true));
    const unregisterButtonTextContent = el('label.event-unregister-button-text', 'Registered');

    this.unregisterButton = el('button.event-control unregister-button', [registeredView, unregisterButtonTextContent]);
    this.unregisterButton.addEventListener('click', this.deleteEventRegistration.bind(this));

    mount(this.el, this.unregisterButton);

  }

  mountCancelButton() {

    this.cancelButton = el('button.event-control', 'Cancel Event');
    this.cancelButton.addEventListener('click', this.cancelEvent.bind(this));

    mount(this.el, this.cancelButton);

  }

  mountJoinButton() {

    this.joinButton = el('button.event-control', this.getJoinButtonText());
    this.joinButton.addEventListener('click', this.handleJoinButtonClick.bind(this));

    mount(this.el, this.joinButton);

  }

  mountEndedButton() {

    this.endedButton = el('button.event-control.ended-event-control', 'Event ended');

    mount(this.el, this.endedButton);

  }

  mountCancelledButton() {

    this.cancelledButton = el('button.event-control.ended-event-control', 'Event cancelled');

    mount(this.el, this.cancelledButton);

  }

  unmountRegisterButton() {

    unmount(this.el, this.registerButton);

    this.registerButton = null;

  }

  tryUnmountRegisterButton() {

    if (this.registerButton === null) {
      return;
    }

    this.unmountRegisterButton();

  }

  unmountUnregisterButton() {

    unmount(this.el, this.unregisterButton);

    this.unregisterButton = null;

  }

  tryUnmountUnregisterButton() {

    if (this.unregisterButton === null) {
      return;
    }

    this.unmountUnregisterButton();

  }

  unmountJoinButton() {

    unmount(this.el, this.joinButton);

  }

  mountControl() {

    const event = this.event;

    if (event.status === eventStatuses.ENDED) {
      this.mountEndedButton();
    } else if (event.status === eventStatuses.CANCELLED) {
      this.mountCancelledButton();
    } else if (event.status === eventStatuses.STARTED) {
      this.mountJoinButton();
    } else {
      if (this.alreadyRegistered === true) {
        this.mountUnregisterButton();
      } else {
        this.mountRegisterButton();
      }
    }

  }

  onunmount() {

    application.eventSystem.internal.offAllOn(this);

  }

  registerEventHandlers() {

    const postId = this.event.postId;

    application.eventSystem.internal.eventFor(this, `${postId}:${externalEventCodes.STARTUP_EVENT_STARTED}`, this.handleStartupEventStartedEvent.bind(this));
    application.eventSystem.internal.eventFor(this, `${postId}:${externalEventCodes.STARTUP_EVENT_ENDED}`, this.handleStartupEventEndedEvent.bind(this));

  }

  initialize() {

    this.registerEventHandlers();
    this.mountControl();

  }

  displayToastMessage(message, type) {

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: message,
      seconds: 5,
      type
    });

  }

  async createEventRegistration() {

    if (this.mode === modes.READ) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_AUTHENTICATOR);
      return;
    }

    try {

      await serverInterface.startup.community.events.createEventRegistration(this.postId);

      this.displayToastMessage('Successfully registered.', 'info');

      this.unmountRegisterButton();
      this.mountUnregisterButton();

    } catch (exception) {

      if (exception.code === 'ALREADY_REGISTERED_TO_STARTUP_EVENT') {
        this.displayToastMessage('You are already registered to the event.', 'error');
        return;
      }

      this.displayToastMessage('Unable to register. Contact administrator!', 'error');

    }

  }

  async deleteEventRegistration() {

    try {

      await serverInterface.startup.community.events.deleteEventRegistrationByIdAndUserId(this.postId);

      this.unmountUnregisterButton();
      this.mountRegisterButton();

      this.displayToastMessage('Successfully unregistered from the event.', 'info');

    } catch (exception) {

      this.displayToastMessage('Unable to unregister. Contact administrator!', 'error');

    }

  }

  isOwner() {

    if (this.userId === this.event.ownerId) {
      return true;
    }

    return false;

  }

  joinLiveStreaming(postId) {

    application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
      to: `/community/${postId}/live`
    });

  }

  handleJoinButtonClick() {

    if (this.mode === modes.READ) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_AUTHENTICATOR);
      return;
    }

    const event = this.event;
    const location = event.location;
    const locationType = location.type;
    const locationValue = location.value;

    if (locationType === eventLocationTypes.HELPTAP_LIVESTREAM) {
      this.joinLiveStreaming(event.postId);
      return;
    }

    const result = checkValueIsURL(locationValue);

    if (result.valid === true) {
      window.open(result.url, '_blank');
      return;
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_EVENT_JOINING_INSTRUCTIONS, {
      instruction: locationValue
    });

  }

  async cancelEvent() {

    const result = await serverInterface.startup.community.events.cancelEventByPostId(this.postId);

    if (result === true) {
      return this.displayToastMessage('Successfully cancelled event', 'info');
    }

    return this.displayToastMessage('Unable to cancel. Contact administrator!', 'error');

  }

  switchToJoinButton() {

    this.tryUnmountRegisterButton();
    this.tryUnmountUnregisterButton();
    this.mountJoinButton();

  }

  switchToEndedButton() {

    this.unmountJoinButton();
    this.mountEndedButton();

  }

  handleStartupEventStartedEvent(data) {

    this.event = data.event;

    this.switchToJoinButton();

  }

  handleStartupEventEndedEvent(data) {

    this.event = data.event;

    this.switchToEndedButton();

  }

};

export default EventCTAButton;